import { Tooltip } from 'antd';
import cn from 'classnames';

import { useOverflowTooltip } from '../hooks';

import type { TooltipProps } from 'antd';
import type { FC, ReactNode } from 'react';

import '../styles/OverflowTooltip.scss';

interface OverflowTooltipProps {
  children: ReactNode;
  title: ReactNode;
  className?: string;
  tooltipClassName?: string;
  placement?: TooltipProps['placement'];
  disabled?: boolean;
  testId?: string;
}

export const OverflowTooltip: FC<OverflowTooltipProps> = ({
  children,
  title,
  className,
  tooltipClassName,
  placement,
  disabled = false,
  testId,
}) => {
  const { isOpen, contentRef, openHandler, closeHandler } = useOverflowTooltip(
    disabled,
    typeof title === 'string' ? title : undefined
  );

  // The caller's `className` owns truncation (single-line ellipsis or
  // multiline clamp); this component only adds overflow detection and the
  // controlled tooltip. The text always stays in the DOM, so it remains
  // selectable and readable by assistive tech.
  const content = (
    <span
      ref={contentRef}
      className={cn('OverflowTooltip', className)}
      data-testid={testId}
      onPointerEnter={openHandler}
      onPointerLeave={closeHandler}
      onFocus={openHandler}
      onBlur={closeHandler}
    >
      {children}
    </span>
  );

  // The antd Tooltip (with its rc-trigger + portal machinery) mounts only after
  // hovering/focusing an element that is actually truncated. Cells whose text
  // fits render a plain <span> — no per-cell Tooltip, ResizeObserver, or portal
  // — which keeps hot table cells cheap and avoids ant-tooltip-content layout
  // shifts for text that is not clipped. Closing unmounts the Tooltip outright,
  // destroying the popup immediately instead of leaving a stuck overlay.
  if (!isOpen) {
    return content;
  }

  return (
    <Tooltip
      open
      title={title}
      placement={placement}
      destroyOnHidden
      mouseLeaveDelay={0}
      classNames={{ body: cn('OverflowTooltip__tooltip', tooltipClassName) }}
    >
      {content}
    </Tooltip>
  );
};
